/**
 * RunAI OS Layout — AI Operating System shell
 * Route: /dashboard
 *
 * TEMA İZOLASYONU:
 * - OS kendi tema state'ini tutar (OSThemeContext → localStorage 'os-theme')
 * - body.light class'ına DOKUNMAZ
 * - [data-area="os"] scope'unda çalışır, theme-os.css'ten beslenir
 * - isDark=false → data-area="os" div'ine className="light" eklenir
 * - Landing page ve classic dashboard ETKİLENMEZ
 */
import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { AgentProvider } from '../../contexts/AgentContext'
import { ChatProvider } from '../../contexts/ChatContext'
import { FocusModeProvider } from '../../contexts/FocusModeContext'
import { SkillsProvider } from '../../contexts/SkillsContext'
import { TrainingProvider } from '../../contexts/TrainingContext'
import { useOSKeyboardShortcuts } from '../../hooks/useOSKeyboardShortcuts'
import { useResponsive } from '../../hooks/useResponsive'
import { useToast } from '../../hooks/useToast'
import {
    Dock, MenuBar,
    NotificationCenter, NotificationProvider, NotificationToast,
    OSThemeProvider,
    WindowManagerProvider
} from '../os'
import ApiErrorToast from '../os/ApiErrorToast'
import { ControlCenter as CC } from '../os/ControlCenter/ControlCenter'
import { getWallpaper, type WallpaperId } from '../os/DesktopHero/wallpaperConfig'
import { useDockPosition } from '../os/Dock/useDockPosition'
import WelcomeToast from '../os/Notifications/WelcomeToast'
import { useOSTheme } from '../os/OSThemeContext'
import ScreenshotTool from '../os/Screenshot/ScreenshotTool'
import { AlertCenter } from '../os/SideChat/AlertCenter'
import SideChat from '../os/SideChat/SideChat'
import Spotlight from '../os/Spotlight'

function OSShell() {
  const { isDark, toggle } = useOSTheme()
  const { position } = useDockPosition()
  const { isSmall } = useResponsive()
  const toast = useToast()
  useOSKeyboardShortcuts()
  const [spotlightOpen, setSpotlightOpen] = useState(false)
  const [ncOpen, setNcOpen] = useState(false)
  const [ccOpen, setCcOpen] = useState(false)
  const [wallpaper, setWallpaper] = useState<WallpaperId>(() => {
    const saved = localStorage.getItem('os-wallpaper') as WallpaperId
    // Eski wallpaper ID'leri yeni sisteme map et
    const legacyMap: Record<string, WallpaperId> = {
      'aurora': 'midnight', 'aurora-dance': 'cosmos', 'dark-space': 'void',
      'mesh': 'cosmos', 'minimal': 'void', 'neon': 'ocean', 'sunset': 'ember',
      'default-dark': 'midnight',
    }
    if (saved && legacyMap[saved]) return legacyMap[saved]
    if (saved && ['midnight','ocean','forest','slate','cosmos','ember','void'].includes(saved)) return saved as WallpaperId
    return 'midnight'
  })

  // OS-level drag payload — shared across all windows
  const [osDragPayload, setOsDragPayload] = useState<{
    type: 'file'
    fileId: number
    filename: string
    streamUrl: string
    mimeType?: string
    mediaType?: string
  } | null>(null)

  useEffect(() => {
    const startHandler = (e: Event) => {
      setOsDragPayload((e as CustomEvent).detail)
    }
    const endHandler = () => {
      setOsDragPayload(null)
    }
    window.addEventListener('runai:drag-start', startHandler)
    window.addEventListener('runai:drag-end', endHandler)
    return () => {
      window.removeEventListener('runai:drag-start', startHandler)
      window.removeEventListener('runai:drag-end', endHandler)
    }
  }, [])

  // Store in localStorage so windows can access it
  useEffect(() => {
    if (osDragPayload) {
      localStorage.setItem('runai_drag_payload', JSON.stringify(osDragPayload))
    } else {
      localStorage.removeItem('runai_drag_payload')
    }
  }, [osDragPayload])

  useEffect(() => {
    const handler = (e: any) => setWallpaper(e.detail)
    // Hem eski hem yeni event adını dinle
    window.addEventListener('wallpaper-change', handler)
    window.addEventListener('runai:wallpaper-change', handler)
    return () => {
      window.removeEventListener('wallpaper-change', handler)
      window.removeEventListener('runai:wallpaper-change', handler)
    }
  }, [])

  // aurora-dance kaldırıldı — bu useEffect artık gerekli değil

  // --os-* var'larını :root'a set et — fixed positioned element'ler (SideChat, Spotlight) için
  // Sadece --os-* var'ları, site var'larına DOKUNMAZ
  // KURAL 58: tek kaynak — burada tanımlı olmayan renk component'te hardcoded YASAK
  useEffect(() => {
    const r = document.documentElement
    const vars: Record<string, [string, string]> = {
      // ── Backgrounds ────────────────────────────────────────────────────
      '--os-bg':          ['#0b0e14', '#f0f2f7'],
      '--os-surface':     ['rgba(22,27,34,0.88)', 'rgba(255,255,255,0.92)'],
      '--os-surface-2':   ['rgba(30,36,46,0.92)', 'rgba(245,247,250,0.95)'],
      '--os-card':        ['rgba(22,27,34,0.75)', 'rgba(255,255,255,0.85)'],
      '--os-card-2':      ['rgba(30,36,46,0.85)', 'rgba(248,249,252,0.9)'],
      // ── Borders ────────────────────────────────────────────────────────
      '--os-border':      ['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.08)'],
      '--os-border-strong':['rgba(255,255,255,0.14)', 'rgba(0,0,0,0.14)'],
      '--os-card-border': ['rgba(255,255,255,0.06)', 'rgba(0,0,0,0.06)'],
      // ── Text ───────────────────────────────────────────────────────────
      '--os-text':        ['#e6edf3', '#1a1d23'],
      '--os-text-strong': ['#ffffff', '#0a0c10'],
      '--os-muted':       ['#8b949e', '#5a6472'],
      '--os-dim':         ['#484f58', '#9ca3af'],
      // ── Menu / Dock ────────────────────────────────────────────────────
      '--os-mb-bg':       ['rgba(0,0,0,0.55)', 'rgba(255,255,255,0.88)'],
      '--os-dock-bg':     ['rgba(0,0,0,0.35)', 'rgba(255,255,255,0.75)'],
      '--os-dock-pill':   ['rgba(0,0,0,0.3)', 'rgba(255,255,255,0.8)'],
      // ── Window ─────────────────────────────────────────────────────────
      '--os-win-bg':      ['rgba(22,27,34,0.88)', 'rgba(255,255,255,0.95)'],
      // Title bar — KURAL: drag area belirgin olmalı
      // Active window (focused): daha kontrastlı — kullanıcı sürükleneceğini anlasın
      '--os-win-tb':         ['linear-gradient(180deg,rgba(38,44,56,0.95) 0%,rgba(28,33,42,0.92) 100%)',
                              'linear-gradient(180deg,rgba(245,247,250,0.96) 0%,rgba(232,236,242,0.92) 100%)'],
      '--os-win-tb-hover':   ['linear-gradient(180deg,rgba(48,54,68,0.98) 0%,rgba(38,44,56,0.95) 100%)',
                              'linear-gradient(180deg,rgba(232,236,242,1) 0%,rgba(218,224,232,0.96) 100%)'],
      '--os-win-tb-border':  ['rgba(255,255,255,0.10)', 'rgba(0,0,0,0.12)'],
      '--os-win-tb-text':    ['#c9d1d9', '#3a414c'],
      '--os-win-shadow':  ['0 24px 80px rgba(0,0,0,0.55)', '0 8px 40px rgba(0,0,0,0.12)'],
      '--os-win-shadow-focus': ['0 32px 100px rgba(0,0,0,0.7)', '0 16px 60px rgba(0,0,0,0.18)'],
      // ── Inputs / Hover ─────────────────────────────────────────────────
      '--os-input-bg':    ['rgba(255,255,255,0.04)', 'rgba(0,0,0,0.04)'],
      '--os-input-border':['rgba(255,255,255,0.10)', 'rgba(0,0,0,0.12)'],
      '--os-hover':       ['rgba(255,255,255,0.06)', 'rgba(0,0,0,0.05)'],
      '--os-hover-strong':['rgba(255,255,255,0.10)', 'rgba(0,0,0,0.08)'],
      '--os-active':      ['rgba(99,102,241,0.18)', 'rgba(99,102,241,0.12)'],
      // ── Desktop / Wallpaper ────────────────────────────────────────────
      '--os-desktop-bg':  ['linear-gradient(135deg,#0a0a1a 0%,#1a1040 40%,#0d1a2a 100%)', 'linear-gradient(135deg,#e8eaf6 0%,#f3e5f5 40%,#e3f2fd 100%)'],
      '--os-icon-label':  ['rgba(255,255,255,0.85)', '#3a414c'],
      '--os-icon-hover':  ['rgba(255,255,255,0.06)', 'rgba(0,0,0,0.05)'],
      // ── Bubbles (chat) ─────────────────────────────────────────────────
      '--os-bubble-user': ['rgba(99,102,241,0.88)', 'rgba(99,102,241,0.92)'],
      '--os-bubble-text': ['#ffffff', '#ffffff'],
      // ── Semantic / Accent ──────────────────────────────────────────────
      '--os-accent':      ['#818cf8', '#6366f1'],
      '--os-accent-hover':['#a5b4fc', '#4f46e5'],
      '--os-accent-bg':   ['rgba(99,102,241,0.18)', 'rgba(99,102,241,0.12)'],
      '--os-success':     ['#10b981', '#059669'],
      '--os-success-bg':  ['rgba(16,185,129,0.16)', 'rgba(16,185,129,0.10)'],
      '--os-warning':     ['#f59e0b', '#d97706'],
      '--os-warning-bg':  ['rgba(245,158,11,0.16)', 'rgba(245,158,11,0.10)'],
      '--os-danger':      ['#ef4444', '#dc2626'],
      '--os-danger-bg':   ['rgba(239,68,68,0.16)', 'rgba(239,68,68,0.10)'],
      '--os-info':        ['#3b82f6', '#2563eb'],
      '--os-info-bg':     ['rgba(59,130,246,0.16)', 'rgba(59,130,246,0.10)'],
      // ── Elevation (shadows) ────────────────────────────────────────────
      '--os-elevation-1': ['0 2px 8px rgba(0,0,0,0.25)',  '0 2px 8px rgba(0,0,0,0.06)'],
      '--os-elevation-2': ['0 8px 24px rgba(0,0,0,0.35)', '0 8px 24px rgba(0,0,0,0.08)'],
      '--os-elevation-3': ['0 16px 48px rgba(0,0,0,0.45)','0 16px 48px rgba(0,0,0,0.12)'],
      '--os-card-shadow': ['0 8px 32px rgba(0,0,0,0.4)',  '0 8px 32px rgba(0,0,0,0.08)'],
      // ── Wallpaper overlay (light/dark wallpaper karışımı için) ────────
      '--os-wallpaper-overlay': ['rgba(0,0,0,0)', 'rgba(255,255,255,0.35)'],
      '--os-launchpad-bg': ['rgba(0,0,0,0.88)', 'rgba(240,242,247,0.92)'],
    }
    const idx = isDark ? 0 : 1
    for (const [k, v] of Object.entries(vars)) r.style.setProperty(k, v[idx])
    // data-theme attribute (CSS selector ile kullanılabilsin)
    r.setAttribute('data-os-theme', isDark ? 'dark' : 'light')
    return () => {
      for (const k of Object.keys(vars)) r.style.removeProperty(k)
      r.removeAttribute('data-os-theme')
    }
  }, [isDark])

  const effectiveDockPos = isSmall ? 'bottom' : position

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSpotlightOpen(s => !s)
      }
      if ((e.metaKey || e.ctrlKey) && e.code === 'Space') {
        e.preventDefault()
        setSpotlightOpen(s => !s)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Toast event listener — agentBus emit'ine karşılık
  useEffect(() => {
    const handleToast = (e: CustomEvent) => {
      const { type, message } = e.detail
      if (type === 'success') {
        toast.success(message)
      } else if (type === 'error') {
        toast.error(message)
      } else if (type === 'warning') {
        toast.warning?.(message)
      } else {
        toast.info?.(message)
      }
    }
    window.addEventListener('runai:toast', handleToast as EventListener)
    return () => window.removeEventListener('runai:toast', handleToast as EventListener)
  }, [toast])

  // Agent OS control events
  useEffect(() => {
    // Theme change: "change theme to dark/light"
    const themeHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as string
      const wantDark = detail === 'dark' || detail === 'dark mode'
      if ((wantDark && !isDark) || (!wantDark && isDark)) {
        toggle()
      }
    }
    // Spotlight: "open spotlight" or "search for X"
    const spotlightHandler = (_e: Event) => {
      setSpotlightOpen(true)
    }
    window.addEventListener('runai:theme-change', themeHandler)
    window.addEventListener('runai:spotlight-open', spotlightHandler)
    return () => {
      window.removeEventListener('runai:theme-change', themeHandler)
      window.removeEventListener('runai:spotlight-open', spotlightHandler)
    }
  }, [isDark, toggle])

  return (
    <div
      data-area="os"
      className={isDark ? '' : 'light'}
      role="application"
      aria-label="RunAI WebOS"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        ...getWallpaper(wallpaper, isDark),
        color: 'var(--os-text)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        position: 'relative',
        transition: 'background 0.3s',
      }}
    >
      {/* Wallpaper overlay — light mode readability */}
      <div style={{ position: 'absolute', inset: 0, background: 'var(--os-wallpaper-overlay)', pointerEvents: 'none', zIndex: 0 }} aria-hidden="true" />

      {/* Skip to main content — accessibility */}
      <a
        href="#os-main-content"
        style={{
          position: 'absolute', top: -40, left: 0, zIndex: 99999,
          background: 'var(--os-accent)', color: '#fff', padding: '8px 16px',
          borderRadius: '0 0 8px 0', fontSize: 12, fontWeight: 600,
          transition: 'top 0.2s',
        }}
        onFocus={e => { e.currentTarget.style.top = '0' }}
        onBlur={e => { e.currentTarget.style.top = '-40px' }}
      >
        Skip to main content
      </a>

      <header role="banner" aria-label="RunAI OS Menu Bar">
        <MenuBar
          onSpotlightOpen={() => setSpotlightOpen(true)}
          onClockClick={() => setNcOpen(p => !p)}
          onAlertClick={() => setNcOpen(p => !p)}
          onControlCenterOpen={() => setCcOpen(p => !p)}
        />
      </header>

      {/* Dock — macOS gibi float, layout'tan bağımsız, always on top */}
      <nav role="navigation" aria-label="Application Dock" style={{
        position: 'fixed',
        ...(effectiveDockPos === 'bottom' ? { bottom: 8, left: '50%', transform: 'translateX(-50%)' } :
           effectiveDockPos === 'top' ? { top: 48, left: '50%', transform: 'translateX(-50%)' } :
           effectiveDockPos === 'left' ? { left: 8, top: '50%', transform: 'translateY(-50%)' } :
           { right: 8, top: '50%', transform: 'translateY(-50%)' }),
        zIndex: 99999,
      }}>
        <Dock />
      </nav>

      <main id="os-main-content" role="main" aria-label="Desktop" style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <Outlet />
      </main>

      <NotificationToast />
      <AlertCenter />
      <ApiErrorToast />
      <WelcomeToast />
      {/* <OnboardingFlow /> */}
      <NotificationCenter open={ncOpen} onClose={() => setNcOpen(false)} />
      <CC open={ccOpen} onClose={() => setCcOpen(false)} />
      <Spotlight open={spotlightOpen} onClose={() => setSpotlightOpen(false)} />
      <SideChat />
      <ScreenshotTool />
    </div>
  )
}

export default function RunAIOSLayout() {
  return (
    <OSThemeProvider>
      <FocusModeProvider>
      <NotificationProvider>
        <AgentProvider>
          <SkillsProvider>
            <ChatProvider>
              <TrainingProvider>
                <WindowManagerProvider>
                  <OSShell />
                </WindowManagerProvider>
              </TrainingProvider>
            </ChatProvider>
          </SkillsProvider>
        </AgentProvider>
      </NotificationProvider>
      </FocusModeProvider>
    </OSThemeProvider>
  )
}
